"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePrompt = void 0;
const react_1 = require("react");
const lodash_1 = require("lodash");
const event_callback_hook_1 = require("../../utils/event-callback-hook");
function mergePromptsByKey(current, incoming) {
    const incomingKeys = new Set(incoming.map(({ key }) => key));
    return [...current.filter(({ key }) => !incomingKeys.has(key)), ...incoming];
}
function removePromptsByKey(current, removed) {
    const removedKeys = new Set(removed.map(({ key }) => key));
    return current.filter(({ key }) => !removedKeys.has(key));
}
function usePrompt() {
    const [prompts, setPrompts] = (0, react_1.useState)([]);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [prompt, setPrompt] = (0, react_1.useState)();
    const dialogRef = (0, react_1.useRef)(null);
    const submitCallbackRef = (0, react_1.useRef)();
    const closeCallbackRef = (0, react_1.useRef)();
    const promptsRef = (0, react_1.useRef)([]);
    const promptRef = (0, react_1.useRef)();
    const pendingCloseTimeoutRef = (0, react_1.useRef)();
    const cancelPendingClose = (0, event_callback_hook_1.useEventCallback)(() => {
        if (pendingCloseTimeoutRef.current !== undefined) {
            clearTimeout(pendingCloseTimeoutRef.current);
            pendingCloseTimeoutRef.current = undefined;
        }
    });
    const testPrompt = (0, event_callback_hook_1.useEventCallback)(({ key, submitCallback, closeCallback = lodash_1.noop, }) => {
        var _a;
        const prompt = prompts.find((p) => p.key === key);
        if (prompt) {
            setPrompt(prompt);
            promptRef.current = prompt;
            submitCallbackRef.current = submitCallback;
            closeCallbackRef.current = closeCallback;
            (_a = dialogRef.current) === null || _a === void 0 ? void 0 : _a.open();
        }
        else {
            submitCallback(undefined);
        }
    });
    const registerPrompts = (0, event_callback_hook_1.useEventCallback)((prompts) => {
        var _a;
        const activePromptKey = (_a = promptRef.current) === null || _a === void 0 ? void 0 : _a.key;
        const updatedActivePrompt = activePromptKey !== undefined
            ? prompts.find(({ key }) => key === activePromptKey)
            : undefined;
        const nextPrompts = mergePromptsByKey(promptsRef.current, prompts);
        promptsRef.current = nextPrompts;
        setPrompts(nextPrompts);
        if (updatedActivePrompt !== undefined) {
            cancelPendingClose();
            setPrompt(updatedActivePrompt);
            promptRef.current = updatedActivePrompt;
        }
    });
    const unregisterPrompts = (0, event_callback_hook_1.useEventCallback)((prompts) => {
        var _a;
        const nextPrompts = removePromptsByKey(promptsRef.current, prompts);
        const activePromptKey = (_a = promptRef.current) === null || _a === void 0 ? void 0 : _a.key;
        const shouldClose = activePromptKey !== undefined &&
            !nextPrompts.some(({ key }) => key === activePromptKey);
        promptsRef.current = nextPrompts;
        setPrompts(nextPrompts);
        if (shouldClose) {
            cancelPendingClose();
            pendingCloseTimeoutRef.current = setTimeout(() => {
                var _a, _b;
                pendingCloseTimeoutRef.current = undefined;
                const isPromptRegistered = promptsRef.current.some(({ key }) => key === activePromptKey);
                if (isPromptRegistered) {
                    return;
                }
                if (((_a = promptRef.current) === null || _a === void 0 ? void 0 : _a.key) === activePromptKey) {
                    (_b = dialogRef.current) === null || _b === void 0 ? void 0 : _b.close();
                    setPrompt(undefined);
                    promptRef.current = undefined;
                }
            }, 0);
        }
    });
    (0, react_1.useEffect)(() => () => cancelPendingClose(), [cancelPendingClose]);
    const promptConfirm = (0, event_callback_hook_1.useEventCallback)(async (values, formRef) => {
        try {
            setLoading(true);
            const callback = submitCallbackRef.current;
            let result = callback(values, formRef);
            if (result instanceof Promise) {
                result = await result;
            }
            if (result !== true) {
                submitCallbackRef.current = undefined;
                closeCallbackRef.current = undefined;
                prompts.forEach((prompt) => prompt.clearCallback !== undefined && prompt.clearCallback());
            }
            setLoading(false);
            return result;
        }
        catch (err) {
            console.log(err);
            setLoading(false);
        }
    });
    const promptCancel = (0, event_callback_hook_1.useEventCallback)(() => {
        const callback = closeCallbackRef.current;
        callback();
        setLoading(false);
        submitCallbackRef.current = undefined;
        closeCallbackRef.current = undefined;
    });
    const context = (0, react_1.useMemo)(() => ({
        testPrompt,
        registerPrompts,
        unregisterPrompts,
    }), [testPrompt, registerPrompts, unregisterPrompts]);
    return { context, dialogRef, prompt, promptConfirm, promptCancel, loading };
}
exports.usePrompt = usePrompt;
//# sourceMappingURL=prompt-hook.js.map